import type { PropsWithChildren } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue } from '@tamagui/core';

import { Box } from '../Box';
import type { BoxProps } from '../Box';
import { Button } from '../Button';
import type { ButtonProps } from '../Button';
import { SimpleCheckbox } from '../inputs';
import type { SimpleCheckboxProps } from '../inputs';

const { dialog: dialogTokens } = tokens;

const Container = ({ children, $largerThanSM, ...props }: BoxProps) => (
  <Box
    // Applies negative margin to offset bottom padding of DialogContent
    marginTop={-1 * getTokenValue(dialogTokens.margin.mobile.bottom)}
    $largerThanSM={{
      marginTop: -1 * getTokenValue(dialogTokens.margin.desktop.bottom),
      ...$largerThanSM,
    }}
    {...props}
  >
    {children}
  </Box>
);

type ButtonContainerProps = {
  /**
   * Whether to show buttons side by side or vertically stacked on largerThanSM
   * screens (defaults to side by side; small screens always stack)
   */
  buttonPosition?: 'side-by-side' | 'stacked';
};

const ButtonContainer = ({ children, buttonPosition }: PropsWithChildren<ButtonContainerProps>) => (
  <Box
    paddingTop={dialogTokens.padding.mobile.betweenElements}
    paddingBottom={dialogTokens.margin.mobile.bottom}
    paddingHorizontal={dialogTokens.margin.mobile.leftRight}
    $largerThanSM={{
      paddingBottom: dialogTokens.margin.desktop.bottom,
      paddingHorizontal: dialogTokens.margin.desktop.leftRight,
      flexDirection: buttonPosition === 'stacked' ? 'column' : 'row',
    }}
    gap={dialogTokens.padding.actions.default}
  >
    {children}
  </Box>
);

type ActionButtonProps = Omit<ButtonProps, 'size' | 'variant'> &
  Partial<Pick<ButtonProps, 'size' | 'variant'>>;

export type DialogActionsProps = BoxProps &
  ButtonContainerProps & {
    /**
     * Props for the first button (if there is one)
     */
    buttonOneProps?: ActionButtonProps;
    /**
     * Props for the second button (if there is one)
     */
    buttonTwoProps?: ActionButtonProps;
    /**
     * Props for a checkbox. If provided, the checkbox will be
     * displayed above the button container.
     */
    checkboxProps?: SimpleCheckboxProps;
  };

/**
 * Actions for the dialog. Also accepts all BoxProps and passes them to the
 * container.
 */
export const Actions = ({
  buttonPosition,
  buttonOneProps,
  buttonTwoProps,
  checkboxProps,
  ...props
}: DialogActionsProps) => {
  return (
    <Container {...props}>
      {checkboxProps && (
        <Box
          paddingTop={dialogTokens.margin.mobile.top}
          paddingHorizontal={dialogTokens.margin.mobile.leftRight}
          $largerThanSM={{
            paddingTop: 0,
            paddingHorizontal: dialogTokens.margin.desktop.leftRight,
          }}
        >
          <SimpleCheckbox {...checkboxProps} />
        </Box>
      )}

      <ButtonContainer buttonPosition={buttonPosition}>
        {buttonOneProps && (
          <Button
            variant="primary"
            size="small"
            $largerThanSM={{ size: 'medium', flexGrow: 1 }}
            {...buttonOneProps}
          />
        )}
        {buttonTwoProps && (
          <Button
            variant="secondary"
            size="small"
            $largerThanSM={{ size: 'medium', flexGrow: 1 }}
            {...buttonTwoProps}
          />
        )}
      </ButtonContainer>
    </Container>
  );
};
